import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { FileText, X, CheckCircle, AlertTriangle, ExternalLink, ChevronDown, ChevronUp } from 'lucide-react';
import { apiClient } from '@/lib/api-client';
import { useAuth } from '@/contexts/AuthContext';
import { PendingPolicy } from '@/hooks/queries/useUserInitQuery';

interface PolicyAcceptanceModalProps {
  isOpen: boolean;
  onAccept: () => void;
  onDecline: () => void;
  pendingPolicies: PendingPolicy[];
}

export const PolicyAcceptanceModal: React.FC<PolicyAcceptanceModalProps> = ({
  isOpen,
  onAccept,
  onDecline,
  pendingPolicies,
}) => {
  const { logout } = useAuth();
  const [accepting, setAccepting] = useState(false);
  const [selectedPolicies, setSelectedPolicies] = useState<Set<number>>(new Set());
  const [expandedPolicy, setExpandedPolicy] = useState<number | null>(null);

  // Pre-select all policies when they change
  useEffect(() => {
    if (pendingPolicies.length > 0) {
      setSelectedPolicies(new Set(pendingPolicies.map((p) => p.id)));
    }
  }, [pendingPolicies]);

  const handleAccept = async () => {
    if (selectedPolicies.size === 0) {
      return;
    }

    try {
      setAccepting(true);
      const response = await apiClient.post('/cms-pages/policies/accept', {
        cmsPageIds: Array.from(selectedPolicies),
      });

      if (response?.success) {
        onAccept();
      } else {
        throw new Error('Failed to accept policies');
      }
    } catch (error) {
      console.error('Failed to accept policies:', error);
      // Still call onAccept to close modal, but user will be blocked by guard
      onAccept();
    } finally {
      setAccepting(false);
    }
  };

  const handleDecline = () => {
    logout();
    onDecline();
  };

  const handleClose = () => {
    // Closing without accepting = decline
    handleDecline();
  };

  const togglePolicySelection = (policyId: number) => {
    const newSelected = new Set(selectedPolicies);
    if (newSelected.has(policyId)) {
      newSelected.delete(policyId);
    } else {
      newSelected.add(policyId);
    }
    setSelectedPolicies(newSelected);
  };

  const togglePolicyExpansion = (policyId: number) => {
    setExpandedPolicy(expandedPolicy === policyId ? null : policyId);
  };

  // Prevent closing via backdrop click or ESC
  const handleOpenChange = (open: boolean) => {
    if (!open) {
      handleClose();
    }
  };

  if (pendingPolicies.length === 0) {
    return null;
  }

  const allSelected = selectedPolicies.size === pendingPolicies.length;

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent 
        className="max-w-xl flex flex-col gap-0 p-0 overflow-hidden !top-[calc(50%-40px)] sm:!top-[50%] !max-h-[calc(100dvh-120px)] sm:!max-h-[85vh]" 
        onInteractOutside={(e) => e.preventDefault()} 
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        {/* Header - more compact on mobile */}
        <DialogHeader className="px-4 sm:px-6 pt-4 sm:pt-6 pb-3 sm:pb-4 border-b border-border/50 flex-shrink-0">
          <DialogTitle className="flex items-center gap-2 sm:gap-3 text-lg sm:text-xl">
            <div className="flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-amber-500/10 border border-amber-500/20">
              <AlertTriangle className="h-4 w-4 sm:h-5 sm:w-5 text-amber-500" />
            </div>
            <span>Policy Update</span>
          </DialogTitle>
          <DialogDescription className="text-muted-foreground mt-1 sm:mt-2 text-sm">
            Please review and accept the updated {pendingPolicies.length === 1 ? 'policy' : 'policies'} to continue.
          </DialogDescription>
        </DialogHeader>

        {/* Policy List - scrollable area */}
        <div className="flex-1 min-h-0 overflow-y-auto px-4 sm:px-6 py-3 sm:py-4">
          <div className="space-y-2 sm:space-y-3">
            {pendingPolicies.map((policy) => {
              const isSelected = selectedPolicies.has(policy.id);
              const isExpanded = expandedPolicy === policy.id;
              
              return (
                <div
                  key={policy.id}
                  className={`rounded-lg sm:rounded-xl border transition-all duration-200 ${
                    isSelected
                      ? 'border-primary/50 bg-primary/5'
                      : 'border-border/50 bg-muted/20 hover:border-border'
                  }`}
                >
                  {/* Policy Header */}
                  <div className="p-3 sm:p-4">
                    <div className="flex items-start gap-3 sm:gap-4">
                      <Checkbox
                        id={`policy-${policy.id}`}
                        checked={isSelected}
                        onCheckedChange={() => togglePolicySelection(policy.id)}
                        className="mt-0.5 h-5 w-5 rounded border-2 data-[state=checked]:bg-primary data-[state=checked]:border-primary"
                      />
                      <div className="flex-1 min-w-0">
                        <Label
                          htmlFor={`policy-${policy.id}`}
                          className="flex items-center gap-2 cursor-pointer text-sm sm:text-base font-medium leading-tight"
                        >
                          <FileText className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                          {policy.title}
                        </Label>
                        <p className="text-xs text-muted-foreground mt-1">
                          Last updated: {new Date(policy.updated_at).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric'
                          })}
                        </p>
                      </div>
                    </div>
                    
                    {/* Action Buttons */}
                    <div className="flex items-center gap-2 mt-2 sm:mt-3 ml-8 sm:ml-9">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => togglePolicyExpansion(policy.id)}
                        className="h-8 px-3 text-xs font-medium"
                      >
                        {isExpanded ? (
                          <>
                            <ChevronUp className="h-3.5 w-3.5 mr-1.5" />
                            Hide Policy
                          </>
                        ) : (
                          <>
                            <ChevronDown className="h-3.5 w-3.5 mr-1.5" />
                            View Policy
                          </>
                        )}
                      </Button>
                      {policy.pdf_url && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => window.open(policy.pdf_url, '_blank')}
                          className="h-8 px-3 text-xs font-medium text-muted-foreground hover:text-foreground"
                        >
                          <ExternalLink className="h-3.5 w-3.5 mr-1.5" />
                          Open in New Tab
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* Expanded Policy View */}
                  {isExpanded && policy.pdf_url && (
                    <div className="px-4 pb-4">
                      <div className="rounded-lg border border-border/50 bg-background overflow-hidden">
                        <iframe
                          src={policy.pdf_url}
                          className="w-full h-80 border-0"
                          title={policy.title}
                        />
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Footer - always visible, never scrolls */}
        <div className="px-4 sm:px-6 py-3 sm:py-4 border-t border-border/50 bg-muted/30 flex-shrink-0">
          {/* Warning Notice - more compact on mobile */}
          <div className="flex items-start gap-2 sm:gap-3 p-2 sm:p-3 mb-3 sm:mb-4 rounded-lg bg-amber-500/10 border border-amber-500/20">
            <AlertTriangle className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-amber-200/90 leading-snug sm:leading-relaxed">
              You must accept all policies to continue. Declining will log you out of your account.
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-2 sm:gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={handleDecline}
              className="flex-1 h-10 sm:h-11 text-sm"
              disabled={accepting}
            >
              <X className="h-4 w-4 mr-1.5" />
              Decline
            </Button>
            <Button
              type="button"
              variant="green"
              onClick={handleAccept}
              className="flex-1 h-10 sm:h-11 font-semibold text-sm"
              disabled={accepting || selectedPolicies.size === 0}
            >
              {accepting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-current border-t-transparent mr-1.5" />
                  Accepting...
                </>
              ) : (
                <>
                  <CheckCircle className="h-4 w-4 mr-1.5" />
                  {allSelected ? 'Accept All' : `Accept (${selectedPolicies.size})`}
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
